import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  extractDialCode,
  loadRemVoiceScript,
  normalizeDialNumber,
  type RemVoiceApi,
  type RemVoiceDialOpts,
  type RemVoiceEvent,
  type RemVoiceMode,
} from '../lib/remVoice';

export const LINE_BUSY_MESSAGE = 'Line in use — another agent or desk phone is on this number.';

export type RemVoiceDialerLock = 'simple' | 'full' | 'both' | 'crmOnlyDial';

interface RemVoiceSession {
  token: string;
  expiresIn: string;
  jti: string | null;
  crmUserId?: string | null;
  agentId?: string | null;
  receiveEnabled?: boolean;
  dialerMode?: RemVoiceDialerLock;
  inboundRingTarget?: string | null;
  agent: unknown;
  dialerUrl: string;
  embedScriptUrl: string;
  allowedOrigins?: string[];
}

function crmMountMode(lock?: RemVoiceDialerLock | null): RemVoiceMode {
  return lock === 'full' ? 'full' : 'simple';
}

function canSwitchDialerMode(lock?: RemVoiceDialerLock | null): boolean {
  return !lock || lock === 'both';
}

/** Normalize origins for comparison (strip trailing slash). */
function originMatches(allowed: string[] | undefined, current: string): boolean {
  if (!allowed || allowed.length === 0) return true; // don't block if Voice omitted list
  const cur = current.replace(/\/$/, '');
  return allowed.some((o) => {
    const a = String(o || '').replace(/\/$/, '');
    return a === cur;
  });
}

interface RemVoiceContextValue {
  enabled: boolean;
  isReady: boolean;
  isRegistered: boolean;
  status: string | null;
  error: string | null;
  mode: RemVoiceMode;
  dialerMode: RemVoiceDialerLock;
  modeLocked: boolean;
  lineBusy: boolean;
  dial: (number: string, opts?: Pick<RemVoiceDialOpts, 'contactName' | 'leadId' | 'dialCode'>) => Promise<void>;
  hangup: () => void;
  answer: () => void;
  reject: () => void;
  mute: (muted: boolean) => void;
  setMode: (mode: RemVoiceMode) => void;
  revoke: () => Promise<void>;
}

const RemVoiceContext = createContext<RemVoiceContextValue>({
  enabled: false,
  isReady: false,
  isRegistered: false,
  status: null,
  error: null,
  mode: 'simple',
  dialerMode: 'both',
  modeLocked: false,
  lineBusy: false,
  dial: async () => {},
  hangup: () => {},
  answer: () => {},
  reject: () => {},
  mute: () => {},
  setMode: () => {},
  revoke: async () => {},
});

export const useRemVoice = () => useContext(RemVoiceContext);

/** Registered by RemVoiceProvider so App can revoke before destroying the session cookie. */
let remVoiceRevokeHandler: (() => Promise<void>) | null = null;

export async function revokeRemVoiceSession(): Promise<void> {
  if (remVoiceRevokeHandler) {
    await remVoiceRevokeHandler();
  }
}

const REFRESH_MS = 50 * 60 * 1000; // refresh before ~1h JWT expiry

async function waitForRemVoiceEl(timeoutMs = 4000): Promise<HTMLElement> {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    const el = document.getElementById('rem-voice');
    if (el) return el;
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
  }
  throw new Error('REM Voice container #rem-voice not found');
}

interface RemVoiceProviderProps {
  children: React.ReactNode;
  /** When false, dialer stays inactive (logged out). */
  active: boolean;
  /** From /api/auth/me or session — gates minting. */
  remVoiceAgentId?: string | null;
}

export const RemVoiceProvider: React.FC<RemVoiceProviderProps> = ({
  children,
  active,
  remVoiceAgentId,
}) => {
  const [session, setSession] = useState<RemVoiceSession | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mode, setModeState] = useState<RemVoiceMode>('simple');
  const [dialerMode, setDialerMode] = useState<RemVoiceDialerLock>('both');
  const [lineBusy, setLineBusy] = useState(false);
  const remVoiceRef = useRef<RemVoiceApi | null>(null);
  const jtiRef = useRef<string | null>(null);
  const tokenRef = useRef<string | null>(null);
  const mountedRef = useRef(false);

  const enabled = Boolean(active && remVoiceAgentId);

  const handleEvent = useCallback((ev: RemVoiceEvent) => {
    const type = ev.type || null;
    if (type) setStatus(type);
    if (type === 'registered' || type === 'ready') {
      setIsRegistered(true);
      setIsReady(true);
      setError(null);
    }
    if (type === 'error') {
      const code = String((ev as { code?: string }).code || '');
      if (code === 'line_busy') {
        setLineBusy(true);
        setError(LINE_BUSY_MESSAGE);
      } else {
        setError(String((ev as { message?: string }).message || 'Dialer error'));
      }
    }
    if (type === 'ended') {
      setStatus('ended');
      setLineBusy(false);
    }
    if (type === 'answered') {
      setLineBusy(true);
    }
  }, []);

  const mintSession = useCallback(async (): Promise<RemVoiceSession | null> => {
    const res = await fetch('/api/rem-voice/session', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || 'Failed to mint dialer session');
    }
    return data as RemVoiceSession;
  }, []);

  const mountDialer = useCallback(
    async (sess: RemVoiceSession, nextMode: RemVoiceMode) => {
      const pageOrigin = typeof window !== 'undefined' ? window.location.origin : '';
      if (pageOrigin && !originMatches(sess.allowedOrigins, pageOrigin)) {
        const listed = (sess.allowedOrigins || []).join(', ') || '(none)';
        throw new Error(
          `This page origin (${pageOrigin}) is not in the REM Voice API key allowedOrigins [${listed}]. Add the HTTPS CRM origin in REM Voice Admin.`
        );
      }

      const api = await loadRemVoiceScript(sess.embedScriptUrl);
      remVoiceRef.current = api;
      const el = await waitForRemVoiceEl();
      api.mount({
        token: sess.token,
        el,
        mode: nextMode,
        onEvent: handleEvent,
      });
      mountedRef.current = true;
      tokenRef.current = sess.token;
      jtiRef.current = sess.jti;
      if (sess.dialerMode) {
        setDialerMode(sess.dialerMode);
      }
      setIsReady(true);
    },
    [handleEvent]
  );

  const refreshSession = useCallback(async () => {
    if (!enabled) return;
    try {
      const sess = await mintSession();
      if (!sess?.token) return;
      setSession(sess);
      jtiRef.current = sess.jti;
      tokenRef.current = sess.token;
      const nextMode = crmMountMode(sess.dialerMode);
      setModeState(nextMode);
      if (sess.dialerMode) setDialerMode(sess.dialerMode);
      if (mountedRef.current && remVoiceRef.current) {
        remVoiceRef.current.setToken(sess.token);
      } else {
        await mountDialer(sess, nextMode);
      }
      setError(null);
    } catch (err: any) {
      console.error('[RemVoice] refresh failed:', err);
      setError(err?.message || 'Voice session failed');
      setIsReady(false);
    }
  }, [enabled, mintSession, mountDialer]);

  // Initial mount when user has remVoiceAgentId
  useEffect(() => {
    if (!enabled) {
      setSession(null);
      setIsReady(false);
      setIsRegistered(false);
      setStatus(null);
      setLineBusy(false);
      mountedRef.current = false;
      remVoiceRef.current = null;
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const sess = await mintSession();
        if (cancelled || !sess?.token) return;
        setSession(sess);
        const nextMode = crmMountMode(sess.dialerMode);
        setModeState(nextMode);
        if (sess.dialerMode) setDialerMode(sess.dialerMode);
        await mountDialer(sess, nextMode);
        setError(null);
      } catch (err: any) {
        if (!cancelled) {
          console.error('[RemVoice] init failed:', err);
          setError(err?.message || 'Voice not available');
          setIsReady(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- remount when agent mapping / login changes
  }, [enabled, remVoiceAgentId]);

  // Token refresh
  useEffect(() => {
    if (!enabled || !session) return;
    const id = window.setInterval(() => {
      refreshSession();
    }, REFRESH_MS);
    return () => window.clearInterval(id);
  }, [enabled, session, refreshSession]);

  // Shared-line busy poll
  useEffect(() => {
    if (!enabled) {
      setLineBusy(false);
      return;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await fetch('/api/rem-voice/line', { credentials: 'include' });
        const data = await res.json().catch(() => ({}));
        if (cancelled || !res.ok) return;
        setLineBusy(Boolean(data.busy));
      } catch {
        /* ignore poll errors */
      }
    };
    poll();
    const id = window.setInterval(poll, 8000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [enabled]);

  const dial = useCallback(
    async (number: string, opts?: Pick<RemVoiceDialOpts, 'contactName' | 'leadId' | 'dialCode'>) => {
      const normalized = normalizeDialNumber(number);
      if (!normalized) throw new Error('Invalid phone number');
      if (!enabled) throw new Error('Voice not configured');
      if (lineBusy) throw new Error(LINE_BUSY_MESSAGE);

      let api = remVoiceRef.current;
      let sess = session;
      if (!api || !mountedRef.current) {
        sess = session || (await mintSession());
        if (!sess) throw new Error('Voice session unavailable');
        setSession(sess);
        const nextMode = crmMountMode(sess.dialerMode);
        setModeState(nextMode);
        if (sess.dialerMode) setDialerMode(sess.dialerMode);
        await mountDialer(sess, nextMode);
        api = remVoiceRef.current;
      }
      if (!api) throw new Error('Dialer not ready');
      const lock = sess?.dialerMode;
      // embed.js applies dialCode onto the number. We still send UAE as 05… for
      // SIM trunks — passing dialCode "971" with that local form builds a bad
      // MSISDN (e.g. 971050…) and the call never starts. Only send dialCode when
      // the caller already provided one and the number is not a local 0-prefix.
      const localUae = normalized.startsWith('0');
      const dialCode = localUae ? undefined : (opts?.dialCode || extractDialCode(number));
      api.dial(normalized, {
        mode: crmMountMode(lock),
        ...(opts?.contactName ? { contactName: opts.contactName } : {}),
        ...(opts?.leadId ? { leadId: String(opts.leadId) } : {}),
        ...(dialCode ? { dialCode } : {}),
      });
    },
    [enabled, lineBusy, session, mintSession, mountDialer]
  );

  const hangup = useCallback(() => remVoiceRef.current?.hangup(), []);
  const answer = useCallback(() => remVoiceRef.current?.answer(), []);
  const reject = useCallback(() => remVoiceRef.current?.reject(), []);
  const mute = useCallback((muted: boolean) => remVoiceRef.current?.mute(muted), []);

  const setMode = useCallback((next: RemVoiceMode) => {
    if (!canSwitchDialerMode(dialerMode)) return;
    setModeState(next);
    remVoiceRef.current?.setMode(next);
  }, [dialerMode]);

  const revoke = useCallback(async () => {
    const jti = jtiRef.current;
    const token = tokenRef.current;
    mountedRef.current = false;
    remVoiceRef.current = null;
    setSession(null);
    setIsReady(false);
    setIsRegistered(false);
    setLineBusy(false);
    jtiRef.current = null;
    tokenRef.current = null;
    const el = document.getElementById('rem-voice');
    if (el) el.innerHTML = '';

    if (!jti && !token) return;
    try {
      await fetch('/api/rem-voice/revoke', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ jti, token }),
      });
    } catch (err) {
      console.error('[RemVoice] revoke failed:', err);
    }
  }, []);

  useEffect(() => {
    remVoiceRevokeHandler = revoke;
    return () => {
      if (remVoiceRevokeHandler === revoke) remVoiceRevokeHandler = null;
    };
  }, [revoke]);

  const value = useMemo<RemVoiceContextValue>(
    () => ({
      enabled,
      isReady,
      isRegistered,
      status,
      error,
      mode,
      dialerMode,
      modeLocked: !canSwitchDialerMode(dialerMode),
      lineBusy,
      dial,
      hangup,
      answer,
      reject,
      mute,
      setMode,
      revoke,
    }),
    [
      enabled,
      isReady,
      isRegistered,
      status,
      error,
      mode,
      dialerMode,
      lineBusy,
      dial,
      hangup,
      answer,
      reject,
      mute,
      setMode,
      revoke,
    ]
  );

  return (
    <RemVoiceContext.Provider value={value}>{children}</RemVoiceContext.Provider>
  );
};
